// src/pages/FacturacionPresupuestos/FacturacionPresupuestosPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FaFileInvoiceDollar, 
    FaHistory, 
    FaUsers, 
    FaTruckLoading, 
    FaTags, 
    FaCog, 
    FaArrowLeft, 
    FaPlus, 
    FaFileAlt, 
    FaReceipt,
    FaChevronDown,
    FaBolt,
    FaEye
} from 'react-icons/fa';
import { db, functions } from '../../firebase';
import { 
    collection, 
    getDocs, 
    doc, 
    setDoc, 
    deleteDoc, 
    serverTimestamp, 
    query, 
    orderBy 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { PLANTILLAS_BASE, TEMAS_ZEN } from '../../utils/plantillasDocumentos';
import { generarFolioInteligente } from '../../utils/folioFacturacionUtils';
import { sembrarDatosInicialesPrueba, sincronizarModelosOficialesFirestore } from './catalogoInicialDatos';
import DocumentCanvasPreview from './DocumentCanvasPreview';
import DocumentSectionsEditor from './DocumentSectionsEditor';
import DocumentSidebarToolbar from './DocumentSidebarToolbar';
import { imprimirDocumentoComercial, generarHtmlDocumentoImpresion } from './imprimirDocumentoService';
import ModalEnvioSendGrid from './ModalEnvioSendGrid';
import ModalGuardadoExitoso from './ModalGuardadoExitoso';
import HistorialDocumentosTab from './HistorialDocumentosTab';
import CarteraClientesTab from './CarteraClientesTab';
import CarteraProveedoresTab from './CarteraProveedoresTab';
import CatalogoConceptosTab from './CatalogoConceptosTab';
import ConfiguracionFoliosTab from './ConfiguracionFoliosTab';
import Swal from 'sweetalert2';

import './FacturacionPresupuestosPage.css';

const LOCAL_STORAGE_KEY = 'facturacion_borrador_actual_v1';

const FacturacionPresupuestosPage = () => {
    const navigate = useNavigate();

    // Pestaña principal activa: 'editor' | 'historial' | 'clientes' | 'proveedores' | 'conceptos' | 'configuracion'
    const [tabActiva, setTabActiva] = useState('editor');
    // Modo de visualización en el editor: 'secciones' (amplio y sin recortes) o 'lienzo' (hoja física)
    const [modoVistaEditor, setModoVistaEditor] = useState('secciones');

    // Estado del documento actual en el lienzo
    const [documento, setDocumento] = useState(() => {
        try {
            const guardado = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (guardado) {
                return JSON.parse(guardado);
            }
        } catch (e) {
            console.error('Error al recuperar borrador local:', e);
        }
        return JSON.parse(JSON.stringify(PLANTILLAS_BASE.presupuesto));
    });

    // Estados de base de datos
    const [documentos, setDocumentos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [conceptos, setConceptos] = useState([]);
    const [modelosOficiales, setModelosOficiales] = useState(() => {
        try {
            const guardados = localStorage.getItem('plataforma_facturacion_modelos_oficiales');
            return guardados ? JSON.parse(guardados) : PLANTILLAS_BASE;
        } catch {
            return PLANTILLAS_BASE;
        }
    });
    const [configuracion, setConfiguracion] = useState({
        prefijoPresupuesto: 'DH-PR-',
        consecutivoPresupuesto: 32,
        cerosPresupuesto: 3,
        prefijoPago: 'NP-',
        consecutivoPago: 1,
        cerosPago: 3,
        prefijoRemision: 'REM-',
        consecutivoRemision: 1,
        cerosRemision: 3,
        banco: 'BBVA México',
        beneficiario: 'Domus Habita Soluciones Inmobiliarias',
        clabe: '012 180 015487962314',
        cuenta: '1548796231',
        logos: [],
        firmas: []
    });

    // Estados de modales y operaciones
    const [modalSendGridAbierto, setModalSendGridAbierto] = useState(false);
    const [modalGuardadoVisible, setModalGuardadoVisible] = useState(false);
    const [guardadoDetalles, setGuardadoDetalles] = useState({});
    const [docParaEnviar, setDocParaEnviar] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [enviandoSendGrid, setEnviandoSendGrid] = useState(false);
    const [dropdownNuevoAbierto, setDropdownNuevoAbierto] = useState(false);
    const [sembrandoCatalogo, setSembrandoCatalogo] = useState(false);
    const dropdownRef = useRef(null);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickAfuera = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownNuevoAbierto(false);
            }
        };
        document.addEventListener('mousedown', handleClickAfuera);
        return () => document.removeEventListener('mousedown', handleClickAfuera);
    }, []);

    // Auto-guardado local reactivo en cada edición
    useEffect(() => {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(documento));
        } catch (e) {
            console.warn('Error al guardar en localStorage:', e);
        }
    }, [documento]);

    // Cargar datos iniciales desde Firestore y localStorage
    const cargarDatosFirestore = useCallback(async () => {
        try {
            // 0. Carga inmediata desde localStorage
            try {
                const localConf = localStorage.getItem('plataforma_facturacion_config');
                if (localConf) {
                    const parsed = JSON.parse(localConf);
                    setConfiguracion(prev => ({ ...prev, ...parsed }));
                }
            } catch (e) {
                console.warn('Error al leer config de localStorage:', e);
            }

            // 1. Configuración de folios y plataforma
            const configSnap = await getDocs(collection(db, 'plataforma_facturacion_config'));
            if (!configSnap.empty) {
                const configData = configSnap.docs[0].data();
                setConfiguracion(prev => ({ ...prev, ...configData }));

                // Sincronizar automáticamente datos personales y logos con el documento
                setDocumento(prevDoc => {
                    const copia = { ...prevDoc };
                    if (!copia.emisor) copia.emisor = {};
                    if (configData.logos && configData.logos.length > 0 && !copia.emisor.logo1Url) {
                        copia.emisor.logo1Url = configData.logos[0].url;
                    }
                    if (!copia.firmas) copia.firmas = {};
                    if (configData.firmanteNombre && !copia.firmas.emisorNombre) {
                        copia.firmas.emisorNombre = configData.firmanteNombre;
                    }
                    if (configData.firmanteCargo && !copia.firmas.emisorCargo) {
                        copia.firmas.emisorCargo = configData.firmanteCargo;
                    }
                    if (configData.firmas && configData.firmas.length > 0 && !copia.firmas.emisorFirmaUrl) {
                        copia.firmas.emisorFirmaUrl = configData.firmas[0].url;
                    }
                    if (copia.cliente?.contacto && (!copia.firmas.receptorNombre || copia.firmas.receptorNombre === 'Representante Acreditado')) {
                        copia.firmas.receptorNombre = copia.cliente.contacto;
                    }
                    return copia;
                });
            }

            // 2. Documentos emitidos
            const docsQ = query(collection(db, 'plataforma_facturacion_documentos'), orderBy('actualizadoEn', 'desc'));
            const docsSnap = await getDocs(docsQ).catch(async () => {
                // Fallback sin orderBy por si falta índice
                return await getDocs(collection(db, 'plataforma_facturacion_documentos'));
            });
            const docsList = docsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDocumentos(docsList);

            // 3. Clientes
            const cliSnap = await getDocs(collection(db, 'plataforma_facturacion_clientes'));
            const cliList = cliSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setClientes(cliList);

            // 4. Proveedores
            const provSnap = await getDocs(collection(db, 'plataforma_facturacion_proveedores'));
            const provList = provSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setProveedores(provList);

            // 5. Conceptos
            const concSnap = await getDocs(collection(db, 'plataforma_facturacion_conceptos'));
            const concList = concSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setConceptos(concList);

            // 6. Modelos Oficiales en la Nube (Firebase Firestore)
            try {
                const modelos = await sincronizarModelosOficialesFirestore(db);
                if (modelos && Object.keys(modelos).length > 0) {
                    setModelosOficiales(modelos);
                    localStorage.setItem('plataforma_facturacion_modelos_oficiales', JSON.stringify(modelos));
                }
            } catch (errModelos) {
                console.warn('Aviso al sincronizar modelos oficiales desde Firestore:', errModelos);
            }

        } catch (error) {
            console.error('Error al cargar datos de facturación:', error);
        }
    }, []);

    useEffect(() => {
        cargarDatosFirestore();
    }, [cargarDatosFirestore]);

    // Generar nuevo folio según configuración inteligente e irrepetible
    const generarSiguienteFolio = (tipo, cliente = null, fechaEmision = null) => {
        return generarFolioInteligente({
            tipo,
            configuracion,
            cliente,
            fechaEmision: fechaEmision || new Date().toISOString().split('T')[0]
        });
    };

    // Iniciar nuevo documento en blanco según tipo
    const handleNuevoDocumento = (tipo) => {
        const plantilla = (modelosOficiales && modelosOficiales[tipo]) || PLANTILLAS_BASE[tipo] || PLANTILLAS_BASE.presupuesto;
        const nuevoDoc = JSON.parse(JSON.stringify(plantilla));
        nuevoDoc.id = Date.now().toString();
        nuevoDoc.fechaEmision = new Date().toISOString().split('T')[0];
        nuevoDoc.folio = generarSiguienteFolio(tipo, nuevoDoc.cliente, nuevoDoc.fechaEmision);
        
        // Asignar datos bancarios configurados
        nuevoDoc.datosBancarios = {
            banco: configuracion.banco || 'BBVA México',
            beneficiario: configuracion.beneficiario || 'Domus Habita',
            clabe: configuracion.clabe || '',
            cuenta: configuracion.cuenta || ''
        };

        // Asignar primer logo disponible si existe
        if (configuracion.logos && configuracion.logos.length > 0) {
            if (!nuevoDoc.emisor) nuevoDoc.emisor = {};
            nuevoDoc.emisor.logo1Url = configuracion.logos[0].url;
            nuevoDoc.emisor.logoUrl = configuracion.logos[0].url;
        }

        // Asignar firma del emisor configurada y asegurar espacio en blanco para el cliente
        if (!nuevoDoc.firmas) nuevoDoc.firmas = {};
        if (configuracion.firmanteNombre) {
            nuevoDoc.firmas.emisorNombre = configuracion.firmanteNombre;
        }
        if (configuracion.firmanteCargo) {
            nuevoDoc.firmas.emisorCargo = configuracion.firmanteCargo;
        }
        if (configuracion.firmas && configuracion.firmas.length > 0) {
            nuevoDoc.firmas.emisorFirmaUrl = configuracion.firmas[0].url;
            nuevoDoc.firmaUrl = configuracion.firmas[0].url;
        }
        nuevoDoc.firmas.receptorFirmaUrl = ''; // Espacio en blanco garantizado para la firma del cliente

        setDocumento(nuevoDoc);
        setTabActiva('editor');
        setModoVistaEditor('secciones');
    };

    // Guardar documento actual en Firestore con aprendizaje automático de clientes y conceptos
    const handleGuardarDocumento = async () => {
        setGuardando(true);
        try {
            // Comprobación de independencia estricta de folios (Punto 2):
            // Si el documento cargado en el editor ya existe en Firestore pero se le cambió el folio,
            // o si no tiene ID asignado, creamos un nuevo ID independiente para no sobreescribir ni encimar el documento previo.
            const docExistente = documentos.find(d => d.id === documento.id);
            const folioCambio = docExistente && docExistente.folio && documento.folio && (docExistente.folio.trim() !== documento.folio.trim());

            const docId = (folioCambio || !documento.id) ? Date.now().toString() : documento.id;
            const docRef = doc(db, 'plataforma_facturacion_documentos', docId);

            const tipoFinal = (documento.tipoDocumento || 'presupuesto').toLowerCase();
            const categoriaLabel = tipoFinal === 'presupuesto' ? 'Presupuesto' :
                                   tipoFinal === 'pago' ? 'Nota de Pago' : 'Remisión';

            const docData = {
                ...documento,
                id: docId,
                tipoDocumento: tipoFinal,
                categoriaDocumento: categoriaLabel,
                actualizadoEn: new Date().toISOString(),
                guardadoEnTimestamp: serverTimestamp()
            };

            await setDoc(docRef, docData, { merge: true });

            // Si se generó un nuevo ID por cambio de folio, actualizar el estado local
            if (docId !== documento.id) {
                setDocumento(prev => ({ ...prev, id: docId }));
            }

            // 1. Aprendizaje automático de cliente
            if (documento.cliente?.nombre && documento.cliente.nombre.trim()) {
                const clienteExistente = clientes.find(c => 
                    c.nombre?.trim().toLowerCase() === documento.cliente.nombre.trim().toLowerCase()
                );
                const cliId = clienteExistente?.id || `cli_${Date.now()}`;
                const cliRef = doc(db, 'plataforma_facturacion_clientes', cliId);
                const cliData = {
                    id: cliId,
                    nombre: documento.cliente.nombre.trim(),
                    rfc: documento.cliente.rfc || '',
                    contacto: documento.cliente.contacto || '',
                    telefono: documento.cliente.telefono || '',
                    email: documento.cliente.email || '',
                    direccion: documento.cliente.direccion || '',
                    actualizadoEn: new Date().toISOString()
                };
                await setDoc(cliRef, cliData, { merge: true });
            }

            // 2. Aprendizaje automático de conceptos y sus costos
            if (documento.conceptos && Array.isArray(documento.conceptos)) {
                for (const partida of documento.conceptos) {
                    if (partida.descripcion && partida.descripcion.trim()) {
                        const conceptoExistente = conceptos.find(c => 
                            (c.nombre || c.descripcion)?.trim().toLowerCase() === partida.descripcion.trim().toLowerCase()
                        );
                        const concId = conceptoExistente?.id || `conc_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
                        const concRef = doc(db, 'plataforma_facturacion_conceptos', concId);
                        const concData = {
                            id: concId,
                            nombre: partida.descripcion.trim(),
                            descripcion: partida.descripcion.trim(),
                            unidad: partida.unidad || 'Servicio',
                            precioUnitario: Number(partida.precioUnitario) || 0,
                            categoria: documento.tipoDocumento === 'presupuesto' ? 'Presupuesto' : 'Servicio',
                            actualizadoEn: new Date().toISOString()
                        };
                        await setDoc(concRef, concData, { merge: true });
                    }
                }
            }

            // 3. Incrementar el consecutivo de folio si corresponde
            if (documento.tipoDocumento === 'presupuesto') {
                const confRef = doc(db, 'plataforma_facturacion_config', 'general');
                const nuevoConsecutivo = (configuracion.consecutivoPresupuesto || 32) + 1;
                await setDoc(confRef, { consecutivoPresupuesto: nuevoConsecutivo }, { merge: true });
                setConfiguracion(prev => ({ ...prev, consecutivoPresupuesto: nuevoConsecutivo }));
            } else if (documento.tipoDocumento === 'remision') {
                const confRef = doc(db, 'plataforma_facturacion_config', 'general');
                const nuevoConsecutivo = (configuracion.consecutivoRemision || 1) + 1;
                await setDoc(confRef, { consecutivoRemision: nuevoConsecutivo }, { merge: true });
                setConfiguracion(prev => ({ ...prev, consecutivoRemision: nuevoConsecutivo }));
            } else if (documento.tipoDocumento === 'pago') {
                const confRef = doc(db, 'plataforma_facturacion_config', 'general');
                const nuevoConsecutivo = (configuracion.consecutivoPago || 1) + 1;
                await setDoc(confRef, { consecutivoPago: nuevoConsecutivo }, { merge: true });
                setConfiguracion(prev => ({ ...prev, consecutivoPago: nuevoConsecutivo }));
            }

            // Recargar listas locales y mostrar modal personalizado de guardado (Punto 3)
            await cargarDatosFirestore();
            setGuardadoDetalles({
                folio: documento.folio || 'S/F',
                cliente: documento.cliente?.nombre || 'Cliente General',
                tipo: documento.tipoDocumento
            });
            setModalGuardadoVisible(true);
        } catch (error) {
            console.error('Error al guardar documento:', error);
            alert('Ocurrió un error al guardar el documento: ' + error.message);
        } finally {
            setGuardando(false);
        }
    };

    // Sembrar catálogo de pruebas con datos de referencia (Azua, Paseos de la Venta, Fiorelo)
    const handleSembrarCatalogoPruebas = async () => {
        if (!window.confirm('¿Deseas sembrar el catálogo de pruebas con los Clientes, Materiales y Proveedores oficiales de Azua Folio 31, Paseos de la Venta Folio 29 y Fiorelo?')) {
            return;
        }
        setSembrandoCatalogo(true);
        try {
            const res = await sembrarDatosInicialesPrueba(db);
            await cargarDatosFirestore();
            alert(`✅ Catálogo de pruebas cargado con éxito:\n• ${res.clientes} Clientes agregados / actualizados\n• ${res.conceptos} Materiales y Servicios agregados\n• ${res.proveedores} Proveedores registrados`);
        } catch (error) {
            console.error('Error al sembrar catálogo de pruebas:', error);
            alert('Error al sembrar catálogo: ' + error.message);
        } finally {
            setSembrandoCatalogo(false);
        }
    };

    // Enviar o programar con SendGrid (Cloud Function)
    const handleConfirmarEnvioSendGrid = async (datosEnvio) => {
        setEnviandoSendGrid(true);
        try {
            const enviarFn = httpsCallable(functions, 'enviarDocumentoFacturacion');
            const targetDoc = docParaEnviar || documento;
            const htmlPdfOficial = generarHtmlDocumentoImpresion(targetDoc);

            const resultado = await enviarFn({
                datosEnvio,
                documento: targetDoc,
                htmlPdfOficial
            });

            console.log('Resultado SendGrid:', resultado.data);

            const nuevoEstado = datosEnvio.modoEnvio === 'programado'
                ? 'programado'
                : datosEnvio.modoEnvio === 'recurrente'
                    ? 'recurrente'
                    : 'enviado';

            // Actualizar estado del documento con su modalidad real de entrega (Punto 2 y 3)
            if (targetDoc.id) {
                const docRef = doc(db, 'plataforma_facturacion_documentos', targetDoc.id);
                await setDoc(docRef, { 
                    estado: nuevoEstado,
                    programacionEnvio: {
                        modo: datosEnvio.modoEnvio,
                        fechaProgramada: datosEnvio.fechaProgramada || null,
                        horaProgramada: datosEnvio.horaProgramada || null,
                        frecuencia: datosEnvio.frecuencia || null,
                        fechaCorte: datosEnvio.fechaCorte || null,
                        diaCorte: datosEnvio.diaCorte || null,
                        destinatario: datosEnvio.destinatario,
                        asunto: datosEnvio.asunto,
                        actualizadoEn: new Date().toISOString()
                    },
                    ultimoEnvio: {
                        fecha: new Date().toISOString(),
                        destinatario: datosEnvio.destinatario,
                        modo: datosEnvio.modoEnvio,
                        fechaProgramada: datosEnvio.fechaProgramada || null,
                        horaProgramada: datosEnvio.horaProgramada || null,
                        frecuencia: datosEnvio.frecuencia || null,
                        fechaCorte: datosEnvio.fechaCorte || null
                    }
                }, { merge: true });
                await cargarDatosFirestore();
            }

            Swal.fire({
                icon: 'success',
                title: 'Documento Emitido',
                text: datosEnvio.modoEnvio === 'inmediato' 
                    ? `El documento folio ${targetDoc.folio || ''} ha sido enviado exitosamente por correo electrónico con su archivo PDF adjunto.` 
                    : 'El envío programado/recurrente ha sido calendarizado exitosamente.',
                confirmButtonColor: '#2b5329'
            });
            setModalSendGridAbierto(false);
            setDocParaEnviar(null);
        } catch (error) {
            console.error('Error al enviar correo:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error en servicio de correo',
                text: error.message || 'Verifica la dirección de destino o la configuración de correo.',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setEnviandoSendGrid(false);
        }
    };

    // Despachar manualmente los envíos programados y recurrentes pendientes (Punto 3)
    const handleEjecutarEnviosManual = async () => {
        Swal.fire({
            title: 'Despachando Envíos Programados...',
            text: 'Conectando con SendGrid para verificar y enviar documentos pendientes.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            const procesarFn = httpsCallable(functions, 'ejecutarEnviosFacturacionManual');
            const res = await procesarFn();
            const data = res.data || {};
            await cargarDatosFirestore();

            if (data.enviados > 0) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Envíos Procesados!',
                    html: `
                        <div style="font-size: 13.5px; text-align: left; line-height: 1.6;">
                            <p style="color: #166534; font-weight: 700; margin-bottom: 6px;">
                                ✅ ${data.enviados} correo(s) enviado(s) exitosamente vía SendGrid con PDF adjunto.
                            </p>
                            ${data.omitidos > 0 ? `<p style="color: #64748b; margin-bottom: 4px;">⏳ ${data.omitidos} en espera de su hora programada.</p>` : ''}
                            ${data.errores > 0 ? `<p style="color: #dc2626; margin-bottom: 4px;">⚠️ ${data.errores} fallaron al enviar.</p>` : ''}
                        </div>
                    `,
                    confirmButtonColor: '#16a34a'
                });
            } else if (data.omitidos > 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'Envíos en Espera',
                    text: `Hay ${data.omitidos} envío(s) programado(s) que están a la espera de su fecha y hora estipulada.`,
                    confirmButtonColor: '#0284c7'
                });
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'Sin Envíos Pendientes',
                    text: 'No hay documentos pendientes por despachar en este momento.',
                    confirmButtonColor: '#64748b'
                });
            }
        } catch (error) {
            console.error('Error al ejecutar envíos manual:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al Despachar Envíos',
                text: error.message || 'Ocurrió un error al procesar los envíos programados con SendGrid.',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Compartir por WhatsApp con adjunto de PDF oficial y flujo ágil
    const handleCompartirWhatsApp = async (docACompartir = documento) => {
        const docObj = docACompartir || documento;

        // 1. Guardar cambios en Firestore automáticamente para asegurar persistencia
        try {
            if (docObj.id) {
                const docRef = doc(db, 'plataforma_facturacion_documentos', docObj.id);
                await setDoc(docRef, { ...docObj, actualizadoEn: new Date().toISOString() }, { merge: true });
            }
        } catch (eAutoSave) {
            console.warn('Auto-guardado previo a WhatsApp:', eAutoSave);
        }

        // 2. Estructurar mensaje comercial con total y folio
        const total = (docObj.conceptos || []).reduce((acc, c) => acc + ((Number(c.cantidad) || 0) * (Number(c.precioUnitario) || 0)), 0);
        const desc = Number(docObj.descuento) || 0;
        const baseIva = Math.max(0, total - desc);
        const iva = docObj.aplicaIva ? (baseIva * ((Number(docObj.tasaIva) || 16) / 100)) : 0;
        const totalFinal = baseIva + iva;
        const totalStr = totalFinal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const tipoNombre = docObj.tipoDocumento === 'presupuesto' ? 'Presupuesto Formal' :
                           docObj.tipoDocumento === 'pago' ? 'Nota de Pago' : 'Remisión de Cobro';
        const clienteNom = docObj.cliente?.nombre || 'Estimado(a) Cliente';
        const folioDoc = docObj.folio || 'S/F';

        const mensaje = `Hola *${clienteNom}*, te comparto el documento *${tipoNombre}* con folio oficial *${folioDoc}* por un total de *$${totalStr} MXN* emitido por *${docObj.emisor?.nombre || 'Domus Habita'}*.\n\nQuedo a tu disposición para cualquier duda o confirmación. ¡Saludos cordiales!`;

        const telefono = (docObj.cliente?.telefono || '').replace(/\D/g, '');
        let url = `https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`;
        if (telefono && telefono.length >= 10) {
            const telFinal = telefono.length === 10 ? `52${telefono}` : telefono;
            url = `https://api.whatsapp.com/send?phone=${telFinal}&text=${encodeURIComponent(mensaje)}`;
        }

        // 3. Generar PDF oficial vía Cloud Function
        Swal.fire({
            title: 'Generando PDF Oficial...',
            text: 'Preparando el documento idéntico a la vista física para adjuntarlo.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        let pdfFile = null;
        let pdfBlob = null;
        let nombreArchivo = `${tipoNombre.replace(/\s+/g, '_')}_Folio_${folioDoc}.pdf`.replace(/[^a-zA-Z0-9_.-]/g, '_');

        try {
            const htmlPdfOficial = generarHtmlDocumentoImpresion(docObj);
            const obtenerPdfFn = httpsCallable(functions, 'obtenerPdfDocumentoFacturacion');
            const resPdf = await obtenerPdfFn({ documento: docObj, htmlPdfOficial });

            if (resPdf.data && resPdf.data.pdfBase64) {
                const byteCharacters = atob(resPdf.data.pdfBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
                if (resPdf.data.nombreArchivo) {
                    nombreArchivo = resPdf.data.nombreArchivo;
                }
                pdfFile = new File([pdfBlob], nombreArchivo, { type: 'application/pdf' });
            }
        } catch (errorPdf) {
            console.warn('No se pudo generar PDF vía Cloud Function, usando fallback:', errorPdf);
        }

        Swal.close();

        // 4. Compartir según capacidades del dispositivo
        // Caso A: Móvil o Navegador con soporte Web Share API de archivos
        if (pdfFile && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
            try {
                await navigator.share({
                    files: [pdfFile],
                    title: `${tipoNombre} - ${folioDoc}`,
                    text: mensaje
                });
                return;
            } catch (errShare) {
                if (errShare.name !== 'AbortError') {
                    console.warn('Error en Web Share API, procediendo a fallback:', errShare);
                } else {
                    return; // El usuario canceló la hoja de compartir
                }
            }
        }

        // Caso B: Navegador de Escritorio (PC / Mac)
        // Descargamos el archivo PDF listo y abrimos WhatsApp con el chat
        if (pdfBlob) {
            const blobUrl = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = nombreArchivo;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
        }

        window.open(url, '_blank');

        Swal.fire({
            icon: 'success',
            title: 'Documento PDF Oficial Descargado',
            html: `
                <div style="font-size: 13.5px; text-align: left; line-height: 1.6; color: #334155;">
                    <p style="margin-bottom: 8px;">✅ El archivo oficial <strong>${nombreArchivo}</strong> se descargó exitosamente en tu carpeta de <strong>Descargas</strong>.</p>
                    <p style="margin-bottom: 8px;">💬 Se ha abierto la aplicación de WhatsApp con el saludo y resumen comercial listos para enviar.</p>
                    <p style="margin-bottom: 0; color: #15803d; font-weight: 700; background: #f0fdf4; padding: 8px 12px; border-radius: 6px; border: 1px solid #bbf7d0;">
                        📎 Solo arrastra o adjunta el archivo PDF descargado a la conversación de WhatsApp para enviarlo formalmente.
                    </p>
                </div>
            `,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#16a34a',
            allowOutsideClick: false
        });
    };

    // Duplicar documento existente
    const handleDuplicarDocumento = (docOriginal) => {
        const clon = JSON.parse(JSON.stringify(docOriginal));
        clon.id = Date.now().toString();
        clon.fechaEmision = new Date().toISOString().split('T')[0];
        clon.folio = generarSiguienteFolio(clon.tipoDocumento, clon.cliente, clon.fechaEmision);
        clon.estado = 'borrador';
        setDocumento(clon);
        setTabActiva('editor');
    };

    // Convertir documento (ej. presupuesto a remisión)
    const handleConvertirDocumento = (docOriginal, nuevoTipo) => {
        const conv = JSON.parse(JSON.stringify(docOriginal));
        conv.id = Date.now().toString();
        conv.tipoDocumento = nuevoTipo;
        conv.tituloDocumento = nuevoTipo === 'remision' ? 'REMISIÓN DE COBRO' :
                               nuevoTipo === 'pago' ? 'NOTA DE PAGO' : 'PRESUPUESTO';
        conv.fechaEmision = new Date().toISOString().split('T')[0];
        conv.folio = generarSiguienteFolio(nuevoTipo, conv.cliente, conv.fechaEmision);
        conv.estado = 'borrador';
        setDocumento(conv);
        setTabActiva('editor');
    };

    // Eliminar documento con confirmación Zen (Punto 8)
    const handleEliminarDocumento = async (id) => {
        const resConfirm = await Swal.fire({
            title: '¿Eliminar Documento?',
            text: '¿Estás seguro de que deseas eliminar permanentemente este documento del historial?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!resConfirm.isConfirmed) return;
        try {
            await deleteDoc(doc(db, 'plataforma_facturacion_documentos', id));
            await cargarDatosFirestore();
            Swal.fire({
                icon: 'success',
                title: 'Documento Eliminado',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error al eliminar documento:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al eliminar',
                text: error.message || 'No se pudo eliminar el documento.',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Guardar / Eliminar clientes
    const handleGuardarCliente = async (clienteData) => {
        try {
            await setDoc(doc(db, 'plataforma_facturacion_clientes', clienteData.id), clienteData, { merge: true });
            await cargarDatosFirestore();
        } catch (error) {
            console.error('Error al guardar cliente:', error);
        }
    };

    const handleEliminarCliente = async (id) => {
        const resConfirm = await Swal.fire({
            title: '¿Eliminar Cliente?',
            text: '¿Deseas eliminar permanentemente este cliente de la cartera?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!resConfirm.isConfirmed) return;
        try {
            await deleteDoc(doc(db, 'plataforma_facturacion_clientes', id));
            await cargarDatosFirestore();
            Swal.fire({
                icon: 'success',
                title: 'Cliente Eliminado',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error al eliminar cliente:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al eliminar',
                text: error.message || 'No se pudo eliminar el cliente.',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Crear documento para un cliente específico
    const handleCrearDocumentoParaCliente = (cli) => {
        const nuevoDoc = JSON.parse(JSON.stringify(PLANTILLAS_BASE.presupuesto));
        nuevoDoc.id = Date.now().toString();
        nuevoDoc.fechaEmision = new Date().toISOString().split('T')[0];
        nuevoDoc.cliente = {
            nombre: cli.nombre || '',
            rfc: cli.rfc || '',
            contacto: cli.contacto || '',
            telefono: cli.telefono || '',
            email: cli.email || '',
            direccion: cli.direccion || ''
        };
        nuevoDoc.folio = generarSiguienteFolio('presupuesto', nuevoDoc.cliente, nuevoDoc.fechaEmision);
        setDocumento(nuevoDoc);
        setTabActiva('editor');
    };

    // Guardar / Eliminar proveedores
    const handleGuardarProveedor = async (provData) => {
        try {
            await setDoc(doc(db, 'plataforma_facturacion_proveedores', provData.id), provData, { merge: true });
            await cargarDatosFirestore();
        } catch (error) {
            console.error('Error al guardar proveedor:', error);
        }
    };

    const handleEliminarProveedor = async (id) => {
        const resConfirm = await Swal.fire({
            title: '¿Eliminar Proveedor?',
            text: '¿Deseas eliminar permanentemente este proveedor de la cartera?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!resConfirm.isConfirmed) return;
        try {
            await deleteDoc(doc(db, 'plataforma_facturacion_proveedores', id));
            await cargarDatosFirestore();
            Swal.fire({
                icon: 'success',
                title: 'Proveedor Eliminado',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error al eliminar proveedor:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al eliminar',
                text: error.message || 'No se pudo eliminar el proveedor.',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Guardar / Eliminar conceptos
    const handleGuardarConcepto = async (concData) => {
        try {
            await setDoc(doc(db, 'plataforma_facturacion_conceptos', concData.id), concData, { merge: true });
            await cargarDatosFirestore();
        } catch (error) {
            console.error('Error al guardar concepto:', error);
        }
    };

    const handleEliminarConcepto = async (id) => {
        const resConfirm = await Swal.fire({
            title: '¿Eliminar Concepto?',
            text: '¿Deseas eliminar este concepto o partida del catálogo?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!resConfirm.isConfirmed) return;
        try {
            await deleteDoc(doc(db, 'plataforma_facturacion_conceptos', id));
            await cargarDatosFirestore();
            Swal.fire({
                icon: 'success',
                title: 'Concepto Eliminado',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error al eliminar concepto:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al eliminar',
                text: error.message || 'No se pudo eliminar el concepto.',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Guardar configuración general (con tolerancia a fallas y sincronización local)
    const handleGuardarConfiguracion = async (nuevaConfig) => {
        try {
            // 1. Guardar de inmediato en almacenamiento local para disponibilidad garantizada
            try {
                localStorage.setItem('plataforma_facturacion_config', JSON.stringify(nuevaConfig));
            } catch (e) {
                console.warn('Error al guardar config en localStorage:', e);
            }
            
            // 2. Actualizar estado reactivo en memoria
            setConfiguracion(nuevaConfig);

            // 3. Sincronizar reactivamente con el documento en vivo
            setDocumento(prevDoc => {
                const copia = { ...prevDoc };
                if (!copia.emisor) copia.emisor = {};
                if (nuevaConfig.logos && nuevaConfig.logos.length > 0) {
                    copia.emisor.logo1Url = nuevaConfig.logos[0].url;
                }
                if (!copia.firmas) copia.firmas = {};
                if (nuevaConfig.firmanteNombre) {
                    copia.firmas.emisorNombre = nuevaConfig.firmanteNombre;
                }
                if (nuevaConfig.firmanteCargo) {
                    copia.firmas.emisorCargo = nuevaConfig.firmanteCargo;
                }
                if (nuevaConfig.firmas && nuevaConfig.firmas.length > 0) {
                    copia.firmas.emisorFirmaUrl = nuevaConfig.firmas[0].url;
                }
                return copia;
            });

            // 4. Intentar persistir en Firestore
            try {
                await setDoc(doc(db, 'plataforma_facturacion_config', 'general'), nuevaConfig, { merge: true });
            } catch (firestoreErr) {
                console.warn('Aviso: Firestore reportó un detalle de red/permisos, pero los cambios quedaron asegurados localmente:', firestoreErr);
            }
        } catch (error) {
            console.error('Error al guardar configuración:', error);
            throw error;
        }
    };

    // Subir logo rápido desde toolbar
    const handleSubirLogoToolbar = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            const nuevoLogo = { id: Date.now().toString(), url: event.target.result, nombre: file.name };
            const nuevosLogos = [...(configuracion.logos || []), nuevoLogo].slice(0, 3);
            const confActualizada = { ...configuracion, logos: nuevosLogos };
            await handleGuardarConfiguracion(confActualizada);
            setDocumento(prev => ({
                ...prev,
                emisor: { ...prev.emisor, logoUrl: nuevoLogo.url }
            }));
        };
        reader.readAsDataURL(file);
    };

    // Subir firma rápida desde toolbar
    const handleSubirFirmaToolbar = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            const nuevaFirma = { id: Date.now().toString(), url: event.target.result, nombre: file.name };
            const nuevasFirmas = [...(configuracion.firmas || []), nuevaFirma].slice(0, 3);
            const confActualizada = { ...configuracion, firmas: nuevasFirmas };
            await handleGuardarConfiguracion(confActualizada);
            setDocumento(prev => ({
                ...prev,
                firmaUrl: nuevaFirma.url,
                firmas: {
                    ...(prev?.firmas || {}),
                    emisorFirmaUrl: nuevaFirma.url
                }
            }));
        };
        reader.readAsDataURL(file);
    };

    // Calcular total actual del documento activo
    const totalActual = (documento.conceptos || []).reduce((acc, c) => acc + ((Number(c.cantidad) || 0) * (Number(c.precioUnitario) || 0)), 0);
    const totalActualConIva = documento.aplicaIva ? totalActual * 1.16 : totalActual;

    return (
        <div className="facturacion-page-root">
            {/* Header de Navegación Zen */}
            <header className="facturacion-main-nav">
                <div className="facturacion-nav-brand">
                    <button 
                        type="button" 
                        className="facturacion-back-btn"
                        onClick={() => navigate('/configuracion-plataforma')}
                        title="Volver a la Configuración de Plataforma"
                    >
                        <FaArrowLeft /> Volver
                    </button>
                    <h1 className="facturacion-brand-title">
                        <FaFileInvoiceDollar style={{ color: '#2b5329' }} /> Facturación y Presupuestos
                    </h1>
                </div>

                {/* Pestañas de Navegación con Colores Temáticos Distintivos (Punto 3) */}
                <nav className="facturacion-nav-tabs">
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-editor ${tabActiva === 'editor' ? 'active' : ''}`}
                        onClick={() => setTabActiva('editor')}
                    >
                        <FaFileAlt /> Editor de Lienzo
                    </button>
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-historial ${tabActiva === 'historial' ? 'active' : ''}`}
                        onClick={() => setTabActiva('historial')}
                    >
                        <FaHistory /> Historial ({documentos.length})
                    </button>
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-clientes ${tabActiva === 'clientes' ? 'active' : ''}`}
                        onClick={() => setTabActiva('clientes')}
                    >
                        <FaUsers /> Clientes ({clientes.length})
                    </button>
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-proveedores ${tabActiva === 'proveedores' ? 'active' : ''}`}
                        onClick={() => setTabActiva('proveedores')}
                    >
                        <FaTruckLoading /> Proveedores ({proveedores.length})
                    </button>
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-conceptos ${tabActiva === 'conceptos' ? 'active' : ''}`}
                        onClick={() => setTabActiva('conceptos')}
                    >
                        <FaTags /> Conceptos ({conceptos.length})
                    </button>
                    <button 
                        type="button" 
                        className={`facturacion-nav-tab-btn tab-configuracion ${tabActiva === 'configuracion' ? 'active' : ''}`}
                        onClick={() => setTabActiva('configuracion')}
                    >
                        <FaCog /> Folios & Cuentas
                    </button>
                </nav>
            </header>

            {/* Área de Trabajo según pestaña activa */}
            <div className="facturacion-workspace-area">
                {tabActiva === 'editor' && (
                    <>
                        {modoVistaEditor === 'secciones' && (
                            <div className="facturacion-management-view no-print">
                                <DocumentSectionsEditor 
                                    documento={documento}
                                    setDocumento={setDocumento}
                                    onGuardar={handleGuardarDocumento}
                                    onVerVistaPrevia={() => setModoVistaEditor('lienzo')}
                                    onAbrirSendGrid={() => {
                                        setDocParaEnviar(documento);
                                        setModalSendGridAbierto(true);
                                    }}
                                    onCompartirWhatsApp={() => handleCompartirWhatsApp(documento)}
                                    onImprimir={() => imprimirDocumentoComercial(documento)}
                                    onNuevoDocumento={handleNuevoDocumento}
                                    guardando={guardando}
                                    configuracion={configuracion}
                                    modelosOficiales={modelosOficiales}
                                />
                            </div>
                        )}

                        <div className={`facturacion-canvas-container ${modoVistaEditor === 'secciones' ? 'canvas-print-only' : ''}`}>
                            <DocumentCanvasPreview 
                                documento={documento}
                                setDocumento={setDocumento}
                                onGuardar={handleGuardarDocumento}
                                onAbrirSendGrid={() => {
                                    setDocParaEnviar(documento);
                                    setModalSendGridAbierto(true);
                                }}
                                onConvertirDocumento={handleConvertirDocumento}
                                onCompartirWhatsApp={() => handleCompartirWhatsApp(documento)}
                                guardando={guardando}
                            />
                        </div>

                        {modoVistaEditor === 'lienzo' && (
                            <DocumentSidebarToolbar 
                                documento={documento}
                                setDocumento={setDocumento}
                                onGuardar={handleGuardarDocumento}
                                onAbrirSendGrid={() => {
                                    setDocParaEnviar(documento);
                                    setModalSendGridAbierto(true);
                                }}
                                onCompartirWhatsApp={() => handleCompartirWhatsApp(documento)}
                                clientesGuardados={clientes}
                                conceptosGuardados={conceptos}
                                guardando={guardando}
                                logosDisponibles={configuracion.logos || []}
                                firmasDisponibles={configuracion.firmas || []}
                                onSubirLogo={handleSubirLogoToolbar}
                                onSubirFirma={handleSubirFirmaToolbar}
                            />
                        )}
                    </>
                )}

                {tabActiva === 'historial' && (
                    <div className="facturacion-management-view">
                        <HistorialDocumentosTab 
                            documentos={documentos}
                            onNuevoDocumento={handleNuevoDocumento}
                            onEditarDocumento={(docSel) => {
                                setDocumento(docSel);
                                setTabActiva('editor');
                            }}
                            onDuplicarDocumento={handleDuplicarDocumento}
                            onConvertirDocumento={handleConvertirDocumento}
                            onEliminarDocumento={handleEliminarDocumento}
                            onCompartirWhatsApp={handleCompartirWhatsApp}
                            onAbrirSendGrid={(docSel) => {
                                setDocParaEnviar(docSel);
                                setModalSendGridAbierto(true);
                            }}
                            onImprimirDocumento={imprimirDocumentoComercial}
                            onEjecutarEnviosManual={handleEjecutarEnviosManual}
                        />
                    </div>
                )}

                {tabActiva === 'clientes' && (
                    <div className="facturacion-management-view">
                        <CarteraClientesTab 
                            clientes={clientes}
                            onGuardarCliente={handleGuardarCliente}
                            onEliminarCliente={handleEliminarCliente}
                            onCrearDocumentoParaCliente={handleCrearDocumentoParaCliente}
                        />
                    </div>
                )}

                {tabActiva === 'proveedores' && (
                    <div className="facturacion-management-view">
                        <CarteraProveedoresTab 
                            proveedores={proveedores}
                            onGuardarProveedor={handleGuardarProveedor}
                            onEliminarProveedor={handleEliminarProveedor}
                        />
                    </div>
                )}

                {tabActiva === 'conceptos' && (
                    <div className="facturacion-management-view">
                        <CatalogoConceptosTab 
                            conceptos={conceptos}
                            onGuardarConcepto={handleGuardarConcepto}
                            onEliminarConcepto={handleEliminarConcepto}
                        />
                    </div>
                )}

                {tabActiva === 'configuracion' && (
                    <div className="facturacion-management-view">
                        <ConfiguracionFoliosTab 
                            configuracion={configuracion}
                            onGuardarConfiguracion={handleGuardarConfiguracion}
                            guardando={guardando}
                        />
                    </div>
                )}
            </div>

            {/* Modal de Guardado Exitoso con Reloj Regresivo (Punto 3) */}
            <ModalGuardadoExitoso 
                isOpen={modalGuardadoVisible}
                onClose={() => setModalGuardadoVisible(false)}
                detalles={guardadoDetalles}
                tiempoSegundos={6}
            />

            {/* Modal de Envío SendGrid */}
            {modalSendGridAbierto && (
                <ModalEnvioSendGrid 
                    isOpen={modalSendGridAbierto}
                    onClose={() => {
                        setModalSendGridAbierto(false);
                        setDocParaEnviar(null);
                    }}
                    documento={docParaEnviar || documento}
                    totalFinal={
                        docParaEnviar 
                            ? (docParaEnviar.conceptos || []).reduce((acc, c) => acc + ((Number(c.cantidad) || 0) * (Number(c.precioUnitario) || 0)), 0) * (docParaEnviar.aplicaIva ? 1.16 : 1)
                            : totalActualConIva
                    }
                    onConfirmarEnvio={handleConfirmarEnvioSendGrid}
                    enviando={enviandoSendGrid}
                />
            )}

            {/* Widget Flotante Persistente para Alternar Secciones y Lienzo en Todo Momento (Punto 5) */}
            {tabActiva === 'editor' && (
                <div className="facturacion-floating-view-switch no-print" title="Alternar entre Secciones y Lienzo en cualquier nivel de scroll">
                    <button
                        type="button"
                        className={`btn-floating-switch ${modoVistaEditor === 'secciones' ? 'active-secciones' : ''}`}
                        onClick={() => setModoVistaEditor('secciones')}
                        title="Ir al Editor por Secciones"
                    >
                        <FaFileAlt /> Secciones
                    </button>
                    <button
                        type="button"
                        className={`btn-floating-switch ${modoVistaEditor === 'lienzo' ? 'active-lienzo' : ''}`}
                        onClick={() => setModoVistaEditor('lienzo')}
                        title="Ver Hoja Física / Lienzo Completo"
                    >
                        <FaEye /> Lienzo
                    </button>
                </div>
            )}
        </div>
    );
};

export default FacturacionPresupuestosPage;
